import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import Explorer from "../components/Explorer";

import PopularTags from "../components/PopularTags";
import Layout from '../components/Layout';

const SITE_URL = import.meta.env?.VITE_SITE_URL || "https://lenz3.com";
const COLLECTION_LD_SCRIPT_ID = "explorer-collection-ld";

type Props = {};

const ExplorerPage = ({}: Props) => {
  const [searchParams] = useSearchParams();
  const tagNames = searchParams.getAll("tag").filter(Boolean).map((t) => t.trim());
  const hasTags = tagNames.length > 0;

  const title = hasTags
    ? `${tagNames.map((t) => `#${t}`).join(" ")} - اکسپلور | لنزسه`
    : "اکسپلور پست‌های ورزشی — لنزسه";
  const description = hasTags
    ? `پست‌ها و محتوای ورزشی با هشتگ‌های ${tagNames.join("، ")} در لنزسه.`
    : "کاوش پست‌ها و هشتگ‌های ورزشی در پلتفرم لنزسه. اکسپلور تازه‌ترین پست‌ها، استوری‌ها و خبرهای فوتبال و ورزش.";

  // Canonical: stable URL ordering keeps Google from treating /?tag=a&tag=b
  // and /?tag=b&tag=a as duplicate pages competing for ranking.
  const canonical = hasTags
    ? `${SITE_URL}/explorer?${tagNames
        .slice()
        .sort((a, b) => a.localeCompare(b))
        .map((t) => `tag=${encodeURIComponent(t)}`)
        .join("&")}`
    : `${SITE_URL}/explorer`;

  // CollectionPage JSON-LD signals "this is a hashtag landing page" — helps Google
  // group it with other tag pages and avoid soft-404 classification on empty tags.
  useEffect(() => {
    const tagItems = tagNames.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: `#${t}`,
      url: `${SITE_URL}/explorer?tag=${encodeURIComponent(t)}`,
    }));
    const ld = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": canonical,
      url: canonical,
      name: title,
      description,
      inLanguage: "fa-IR",
      isPartOf: { "@id": "https://lenz3.com/#website" },
      ...(hasTags && tagItems.length
        ? {
            mainEntity: {
              "@type": "ItemList",
              itemListElement: tagItems,
              numberOfItems: tagItems.length,
            },
          }
        : {}),
    };
    let script = document.getElementById(COLLECTION_LD_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = COLLECTION_LD_SCRIPT_ID;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => script?.remove();
  }, [canonical, title, description, hasTags, tagNames]);

  return (
    <Layout
      title={title}
      description={description}
      canonical={canonical}
      noIndex
    >
      <h1 className="sr-only">{hasTags ? `پست‌های مرتبط با ${tagNames.map((t) => `#${t}`).join("، ")}` : "اکسپلور پست‌ها و هشتگ‌های ورزشی"}</h1>
      <PopularTags activeTags={tagNames} />
      <Explorer tagNames={tagNames} />
    </Layout>
  );
};

export default ExplorerPage;
